/*
  Eyenk right/left eye picker. Markup and rules: snippets/eyenk-rx-picker.liquid.

  It takes over the product form's submit (Add to cart) and the Buy it now button,
  and posts one cart line per chosen power to /cart/add. Same power in both eyes is
  one line with the combined quantity, as the store sold it before.

  When the product offers Subscribe and save (assets/eyenk-subscribe.js) and the
  customer chooses it, the lines carry the subscription properties, the buttons say
  "Subscribe and …", and the SUBSCRIPTION10 code goes on the cart after the add.
*/
if (!customElements.get('eyenk-rx')) {
  customElements.define(
    'eyenk-rx',
    class EyenkRx extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.data = JSON.parse(this.querySelector('[data-rx-json]').textContent);
        // Arabic whenever the page is Arabic: Shopify's /ar, and ar.eyenk.com, where
        // GTranslate serves the English page with lang="ar". Missing keys fall back to English.
        this.arabic = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
        this.t = { ...this.data.strings.en, ...(this.arabic ? this.data.strings.ar : {}) };
        this.byPower = new Map(this.data.variants.map((variant) => [variant.power, variant]));
        this.byId = new Map(this.data.variants.map((variant) => [String(variant.id), variant]));

        this.eyes = {};
        this.querySelectorAll('[data-eye]').forEach((el) => {
          this.eyes[el.dataset.eye] = {
            el,
            select: el.querySelector('[data-rx-power]'),
            qty: el.querySelector('[data-rx-qty]'),
            label: el.querySelector('[data-rx-eye-label]'),
            error: el.querySelector('[data-rx-error]'),
          };
        });
        this.same = this.querySelector('[data-rx-same]');
        this.total = this.querySelector('[data-rx-total]');
        this.subscription = this.querySelector('eyenk-subscribe');

        this.form = document.getElementById(this.dataset.formId);
        this.productForm = this.form && this.form.closest('product-form');
        this.submitButton = this.productForm && this.productForm.querySelector('[type="submit"]');
        this.submitText = this.submitButton && this.submitButton.querySelector('span');
        this.buyNow = this.productForm && this.productForm.querySelector('[data-rx-buy-now]');
        this.buyNowText = this.buyNow && this.buyNow.querySelector('[data-rx-t="buyNow"]');
        this.idInput = this.form && this.form.querySelector('input[name="id"]');
        this.promise = document.getElementById(`EyenkPromise-${this.dataset.section}`);

        // The script writes these texts, so keep GTranslate from rewriting them.
        [this.submitButton, this.promise].forEach((el) => {
          if (!el) return;
          el.classList.add('notranslate');
          el.setAttribute('translate', 'no');
        });
        this.applyText();
        this.restore();

        this.addEventListener('change', (event) => {
          // Purchase options: only the prices and button labels change.
          if (event.target.closest('eyenk-subscribe')) {
            this.update();
            return;
          }
          if (event.target === this.same) {
            this.toggleSame();
            this.track('rx_same_power');
          }
          if (event.target.matches('select')) this.track('rx_power_chosen');
          if (event.target.matches('[data-rx-qty]')) event.target.value = this.boxes(event.target);
          this.clearErrors();
          this.update();
        });
        this.addEventListener('input', (event) => {
          if (event.target.matches('[data-rx-qty]')) this.update();
        });
        this.addEventListener('eyenk-sub:ready', () => this.update());
        this.addEventListener('click', (event) => {
          const step = event.target.closest('[data-rx-step]');
          if (step) {
            event.preventDefault();
            const input = step.parentElement.querySelector('[data-rx-qty]');
            input.value = Math.min(99, Math.max(0, this.boxes(input) + Number(step.dataset.rxStep)));
            this.clearErrors();
            this.update();
            return;
          }
          const pack = event.target.closest('[data-rx-pack]');
          if (pack && !pack.classList.contains('is-active')) {
            pack.href = this.packUrl(pack.href);
            this.track('rx_pack_switch');
          }
        });
        this.querySelectorAll('[data-rx-pack]:not(.is-active)').forEach((pack) => {
          const prefetch = () => {
            if (pack.dataset.prefetched) return;
            pack.dataset.prefetched = 'true';
            const link = document.createElement('link');
            link.rel = 'prefetch';
            link.href = pack.href;
            document.head.appendChild(link);
          };
          pack.addEventListener('pointerenter', prefetch, { once: true });
          pack.addEventListener('touchstart', prefetch, { once: true, passive: true });
        });

        // Capture on the ancestor so these run before product-form.js's own submit handler.
        // The tap is handled too, so nothing can submit the form natively: its variant id
        // is empty until a power is chosen, and a native post would land on an error page.
        if (this.productForm && this.submitButton) {
          this.productForm.addEventListener(
            'click',
            (event) => {
              if (!event.target.closest('[type="submit"]')) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              this.submit(false);
            },
            true
          );
        }
        if (this.productForm) {
          this.productForm.addEventListener(
            'submit',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.submit(false);
            },
            true
          );
        }
        if (this.buyNow) {
          this.buyNow.addEventListener('click', (event) => {
            event.preventDefault();
            this.submit(true);
          });
        }

        this.update();
        this.classList.add('is-ready');
      }

      // Labels the visit in Microsoft Clarity so recordings can be filtered by picker use.
      track(name) {
        try {
          this.tracked = this.tracked || {};
          if (this.tracked[name] || typeof window.clarity !== 'function') return;
          this.tracked[name] = true;
          window.clarity('event', name);
        } catch (error) {}
      }

      // Fixed texts are rendered in English; this sets them in the page's language.
      applyText() {
        const scopes = [this, this.productForm].filter(Boolean);
        scopes.forEach((scope) => {
          scope.querySelectorAll('[data-rx-t]').forEach((el) => {
            const text = this.t[el.dataset.rxT];
            if (text) el.textContent = text;
          });
        });
        this.querySelectorAll('[data-rx-t-aria]').forEach((el) => {
          const text = this.t[el.dataset.rxTAria];
          if (text) el.setAttribute('aria-label', text);
        });
        this.querySelectorAll('[data-rx-pack-count]').forEach((el) => {
          el.textContent = this.t.pack.replace('{n}', el.dataset.rxPackCount);
        });
        this.querySelectorAll('[data-rx-money]').forEach((el) => {
          el.textContent = this.money(Number(el.dataset.rxMoney));
        });
        this.querySelectorAll('[data-rx-save]').forEach((el) => {
          el.textContent = this.t.save.replace('{amount}', this.money(Number(el.dataset.rxSave), true));
        });
        Object.entries(this.eyes).forEach(([eye, { qty }]) => {
          qty.setAttribute('aria-label', `${this.t.boxesLabel} ${this.t[eye]}`);
        });
        this.eyes.left.label.textContent = this.t.left;
      }

      boxes(input) {
        const value = parseInt(input.value, 10);
        return Number.isFinite(value) ? Math.min(99, Math.max(0, value)) : 0;
      }

      setPower(eye, power) {
        if (power && this.byPower.has(power)) this.eyes[eye].select.value = power;
      }

      // Powers come back from the pack toggle (?od=&os=&odq=&osq=&same=), or from a
      // ?variant= link (search, ads), which fills both eyes with that power.
      restore() {
        const params = new URLSearchParams(window.location.search);
        if (params.has('od') || params.has('os')) {
          this.setPower('right', params.get('od'));
          this.setPower('left', params.get('os'));
          if (params.has('odq')) this.eyes.right.qty.value = this.boxes({ value: params.get('odq') });
          if (params.has('osq')) this.eyes.left.qty.value = this.boxes({ value: params.get('osq') });
          this.same.checked = params.get('same') === '1';
        } else if (params.has('variant')) {
          const variant = this.byId.get(params.get('variant'));
          if (variant) {
            this.same.checked = true;
            this.setPower('right', variant.power);
            this.eyes.right.qty.value = 2;
          }
        }
      }

      packUrl(href) {
        const url = new URL(href, window.location.origin);
        ['od', 'os', 'odq', 'osq', 'same', 'variant'].forEach((key) => url.searchParams.delete(key));
        const { right, left } = this.eyes;
        if (right.select.value) url.searchParams.set('od', right.select.value);
        if (left.select.value) url.searchParams.set('os', left.select.value);
        url.searchParams.set('odq', this.boxes(right.qty));
        url.searchParams.set('osq', this.boxes(left.qty));
        if (this.same.checked) url.searchParams.set('same', '1');
        return url.toString();
      }

      // Folding to one column adds the two box counts together; unfolding splits them.
      toggleSame() {
        const { right, left } = this.eyes;
        if (this.same.checked) {
          if (!right.select.value && left.select.value) right.select.value = left.select.value;
          right.qty.value = Math.max(1, this.boxes(right.qty) + this.boxes(left.qty));
        } else {
          const half = Math.max(1, Math.round(this.boxes(right.qty) / 2));
          right.qty.value = half;
          left.qty.value = half;
        }
      }

      activeEyes() {
        return this.same.checked ? ['right'] : ['right', 'left'];
      }

      // One cart line per power; an eye with 0 boxes is skipped.
      lines() {
        const quantities = new Map();
        const errors = [];
        this.activeEyes().forEach((eye) => {
          const { select, qty } = this.eyes[eye];
          const boxes = this.boxes(qty);
          if (boxes === 0) return;
          const variant = this.byPower.get(select.value);
          if (!variant) {
            errors.push(eye);
            return;
          }
          quantities.set(variant.id, (quantities.get(variant.id) || 0) + boxes);
        });
        const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
        if (!items.length && !errors.length) errors.push('none');
        return { items, errors };
      }

      update() {
        const same = this.same.checked;
        this.classList.toggle('is-same', same);
        this.eyes.right.label.textContent = same ? this.t.both : this.t.right;

        let boxes = 0;
        let cents = 0;
        const chosen = [];
        const perEye = [];
        this.activeEyes().forEach((eye) => {
          const { select, qty } = this.eyes[eye];
          const count = this.boxes(qty);
          const variant = this.byPower.get(select.value);
          boxes += count;
          cents += count * (variant ? variant.price : this.data.price);
          if (variant && count > 0) chosen.push(variant);
          if (count > 0) perEye.push(same ? count / 2 : count);
        });

        // Subscribe and save shows both prices and, when chosen, charges 10% less.
        const subscribing = Boolean(this.subscription && this.subscription.subscribing);
        if (this.subscription && this.subscription.setTotal) {
          this.subscription.setTotal(cents, perEye.length ? Math.min(...perEye) : 1, (value) => this.money(value));
        }
        const price = this.money(subscribing ? this.subscription.discounted(cents) : cents);
        this.total.textContent = `${boxes} ${boxes === 1 ? this.t.box : this.t.boxes} · ${price}`;
        if (this.submitText && this.submitButton && !this.submitButton.disabled) {
          const label = subscribing ? this.subscription.t.subscribeAdd : this.t.addToCart;
          this.submitText.textContent = boxes > 0 ? `${label} · ${price}` : label;
        }
        if (this.buyNowText) this.buyNowText.textContent = subscribing ? this.subscription.t.subscribeBuy : this.t.buyNow;
        if (this.idInput) this.idInput.value = chosen.length ? chosen[0].id : '';
        this.showPromise(chosen);
      }

      // The slowest promise among the chosen powers; weight encodes the delivery tier.
      showPromise(chosen) {
        // The instant-delivery banner shows only when every chosen power is same day.
        const sameDay = chosen.length > 0 && chosen.every((variant) => variant.weight < 500);
        document.querySelectorAll('.product-instock-wrapper').forEach((el) => {
          el.classList.toggle('variant-avilable', sameDay);
        });

        if (!this.promise) return;
        if (!chosen.length) {
          this.promise.hidden = true;
          return;
        }
        const slowest = chosen.reduce((a, b) => (b.weight > a.weight ? b : a));
        const template = this.querySelector(`[data-rx-promise-for="${slowest.id}"]`);
        if (template) this.promise.replaceChildren(template.content.cloneNode(true));
        if (this.arabic) this.localizePromise();
        this.promise.hidden = false;
      }

      // delivery-promise renders English; swap in the Arabic text for the same tier.
      // Outside Qatar the badge carries its own Arabic (data-ar-head / data-ar-note) with
      // that market's lead time; the strings in the JSON are Qatar's.
      localizePromise() {
        const badge = this.promise.querySelector('.eyenk-promise');
        if (!badge) return;
        let pair = badge.dataset.arHead ? [badge.dataset.arHead, badge.dataset.arNote] : null;
        if (!pair && this.t.promise) {
          const tier = Object.keys(this.t.promise).find((key) => badge.classList.contains(`eyenk-promise--${key}`));
          if (tier) pair = this.t.promise[tier];
        }
        if (!pair) return;
        const [head, note] = pair;
        const headEl = badge.querySelector('.eyenk-promise__head');
        const noteEl = badge.querySelector('.eyenk-promise__note');
        const leftEl = badge.querySelector('.eyenk-promise__left');
        if (headEl) headEl.textContent = head;
        if (noteEl) noteEl.textContent = note;
        if (leftEl) leftEl.textContent = /1/.test(leftEl.textContent) ? this.t.left1 : this.t.left2;
      }

      clearErrors() {
        Object.values(this.eyes).forEach(({ el, error }) => {
          el.classList.remove('has-error');
          error.hidden = true;
        });
      }

      showErrors(errors) {
        this.clearErrors();
        errors.forEach((key) => {
          const eye = this.eyes[key === 'none' ? 'right' : key];
          let message = this.t.errNone;
          if (key !== 'none') message = this.same.checked ? this.t.errBoth : key === 'right' ? this.t.errRight : this.t.errLeft;
          eye.el.classList.add('has-error');
          eye.error.textContent = message;
          eye.error.hidden = false;
        });
        const first = this.querySelector('.has-error');
        if (first) {
          first.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const select = first.querySelector('select');
          if (select) select.focus({ preventScroll: true });
        }
      }

      setLoading(button, loading) {
        if (!button) return;
        button.classList.toggle('loading', loading);
        if (loading) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
      }

      async submit(buyNow) {
        const { items, errors } = this.lines();
        if (errors.length) {
          this.track('rx_error');
          this.showErrors(errors);
          return;
        }
        const subscribing = Boolean(this.subscription && this.subscription.subscribing);
        if (subscribing) {
          if (!this.subscription.validate()) return;
          const properties = this.subscription.properties();
          items.forEach((item) => {
            item.properties = properties;
          });
        }
        const button = buyNow ? this.buyNow : this.submitButton;
        if (!button || button.getAttribute('aria-disabled') === 'true') return;
        this.setLoading(button, true);
        if (this.productForm && this.productForm.handleErrorMessage) this.productForm.handleErrorMessage();

        const cart = buyNow ? null : document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const result = await response.json();
          if (result.status) {
            if (this.productForm && this.productForm.handleErrorMessage) {
              this.productForm.handleErrorMessage(result.description || result.message);
            }
            this.setLoading(button, false);
            return;
          }

          this.track(buyNow ? 'rx_buy_now' : 'rx_add_to_cart');
          if (subscribing) {
            // The code goes on after the add; the cart sections are redrawn with it.
            // The notification's product section lists only what one add request
            // added, so that one stays from the add.
            const updated = await this.subscription.applyDiscount(body.sections, body.sections_url);
            if (updated && updated.sections && result.sections) {
              Object.entries(updated.sections).forEach(([id, html]) => {
                if (id !== 'cart-notification-product' && html) result.sections[id] = html;
              });
            }
          }
          if (buyNow) {
            window.location.href = this.data.checkoutUrl;
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: items[0].id,
              cartData: result,
            });
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          cart.renderContents({ ...result, key: result.items[0].key, id: result.items[0].id });
          this.showAllInNotification(result);
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        } catch (error) {
          console.error(error);
        }
        this.setLoading(button, false);
      }

      // Dawn's notification shows the one line it was given; list every line just added.
      showAllInNotification(result) {
        const container = document.getElementById('cart-notification-product');
        const html = result.sections && result.sections['cart-notification-product'];
        if (!container || !html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const parts = result.items
          .map((item) => doc.querySelector(`[id="cart-notification-product-${item.key}"]`))
          .filter(Boolean)
          .map((node) => `<div class="eyenk-rx__notify-item">${node.innerHTML}</div>`);
        if (!parts.length) return;
        container.innerHTML = `<div class="eyenk-rx__notify">${parts.join('')}</div>`;
        if (this.arabic) {
          container.querySelectorAll('.product-option dt').forEach((dt) => {
            if (dt.textContent.trim() === this.data.strings.en.optionPower) dt.textContent = this.t.optionPower;
          });
        }
      }

      // dropZeros: "49" rather than "49.00", as money_without_trailing_zeros does.
      // moneyRate converts QAR cents for display only (markets outside Qatar see their own
      // currency or US dollars, see eyenk-currency.liquid); dinar and rial currencies use
      // 3 decimals.
      money(cents, dropZeros = false) {
        const places = this.data.moneyDecimals === 3 ? 3 : 2;
        const minor = Math.round((cents / 100) * (this.data.moneyRate || 1) * 10 ** places);
        const [whole, fraction] = (minor / 10 ** places).toFixed(places).split('.');
        const grouped = (separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
        const decimals = (separator) => (dropZeros && /^0+$/.test(fraction) ? '' : `${separator}${fraction}`);
        if (this.arabic && this.t.currency) return `${grouped(',')}${decimals('.')} ${this.data.currencyAr || this.t.currency}`;
        const format = this.data.moneyFormat || '{{amount}}';
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
          switch (kind) {
            case 'amount_no_decimals':
              return grouped(',');
            case 'amount_with_comma_separator':
              return `${grouped('.')}${decimals(',')}`;
            case 'amount_no_decimals_with_comma_separator':
              return grouped('.');
            default:
              return `${grouped(',')}${decimals('.')}`;
          }
        });
      }
    }
  );
}
